import {ReactNode} from 'react'
import {
  getMappingOwnId,
  getMappingPairIdentity,
  getPageSiblingKey,
  getSettingPosition,
  isAdvancedSettingItem,
  type MappingNode,
  type PageSibling,
  type PageSiblingKind,
  type PageSiblingSource,
  type SettingNode,
} from './channelSettingsSequence'

export {isAdvancedSettingItem}

export type SequenceNodeRegistry = Map<string, ReactNode>

export const createSequenceNodeRegistry = (): SequenceNodeRegistry => new Map()

const pairAliasKey = (pairIdentity: string) => `mapping:pair:${pairIdentity}`

const pairSlotAliasKey = (pairIdentity: string, slotId: number) =>
  `${pairAliasKey(pairIdentity)}:slot:${slotId}`

export const registerSequenceNode = (
  registry: SequenceNodeRegistry,
  enabled: boolean,
  kind: PageSiblingKind,
  source: PageSiblingSource,
  item: SettingNode | MappingNode | null | undefined,
  node: ReactNode
): void => {
  if (
    !enabled ||
    item == null ||
    node == null ||
    node === false ||
    isAdvancedSettingItem(item)
  ) {
    return
  }

  registry.set(getPageSiblingKey(kind, source, item), node)

  if (kind === 'mapping') {
    const mapping = item as MappingNode
    const pairIdentity = getMappingPairIdentity(mapping)
    if (!pairIdentity) {
      return
    }

    const slotId = getMappingOwnId(mapping)
    if (slotId !== null) {
      registry.set(pairSlotAliasKey(pairIdentity, slotId), node)
    }

    const aliasKey = pairAliasKey(pairIdentity)
    if (source === 'other' || !registry.has(aliasKey)) {
      registry.set(aliasKey, node)
    }
  }
}

export const lookupSequenceNode = (
  registry: SequenceNodeRegistry,
  sibling: PageSibling
): ReactNode | undefined => {
  const directKey = getPageSiblingKey(sibling.kind, sibling.source, sibling.item)
  if (registry.has(directKey)) {
    return registry.get(directKey)
  }

  if (sibling.kind === 'mapping') {
    const mapping = sibling.item as MappingNode
    const pairIdentity = getMappingPairIdentity(mapping)
    if (pairIdentity) {
      const slotId = getMappingOwnId(mapping)
      if (slotId !== null) {
        const slotKey = pairSlotAliasKey(pairIdentity, slotId)
        if (registry.has(slotKey)) {
          return registry.get(slotKey)
        }
      }

      const aliasKey = pairAliasKey(pairIdentity)
      if (registry.has(aliasKey)) {
        return registry.get(aliasKey)
      }
    }
  }

  return undefined
}

const toFiniteId = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null
  }
  const id = Number(value)
  return Number.isFinite(id) ? id : null
}

/** Same channel, distinct catalog slots (setting_mapping_id). */
const isDistinctSameSideCatalogSlot = (existing: PageSibling, incoming: PageSibling): boolean => {
  if (existing.source !== incoming.source) {
    return false
  }

  const leftSlot = toFiniteId((existing.item as MappingNode).setting_mapping_id)
  const rightSlot = toFiniteId((incoming.item as MappingNode).setting_mapping_id)
  return leftSlot !== null && rightSlot !== null && leftSlot !== rightSlot
}

/** One visual slot per cross-channel pair code; keep every same-side catalog slot. */
export const collapsePairedMappingSiblings = (siblings: PageSibling[]): PageSibling[] => {
  const pairSlots = new Map<string, PageSibling>()
  const sameSourceExtras = new Map<string, PageSibling[]>()
  const result: PageSibling[] = []

  const dropSameSourceExtras = (pairIdentity: string) => {
    const extras = sameSourceExtras.get(pairIdentity) ?? []
    extras.forEach((extra) => {
      const extraIndex = result.indexOf(extra)
      if (extraIndex >= 0) {
        result.splice(extraIndex, 1)
      }
    })
    sameSourceExtras.delete(pairIdentity)
  }

  siblings.forEach((sibling) => {
    if (sibling.kind !== 'mapping') {
      result.push(sibling)
      return
    }

    const pairIdentity = getMappingPairIdentity(sibling.item as MappingNode)
    if (!pairIdentity) {
      result.push(sibling)
      return
    }

    const existing = pairSlots.get(pairIdentity)
    if (!existing) {
      pairSlots.set(pairIdentity, sibling)
      result.push(sibling)
      return
    }

    const sameSourceKept = [existing, ...(sameSourceExtras.get(pairIdentity) ?? [])].filter(
      (slot) => slot.source === sibling.source
    )
    if (
      sameSourceKept.length > 0 &&
      sameSourceKept.every((slot) => isDistinctSameSideCatalogSlot(slot, sibling))
    ) {
      const extras = sameSourceExtras.get(pairIdentity) ?? []
      extras.push(sibling)
      sameSourceExtras.set(pairIdentity, extras)
      result.push(sibling)
      return
    }

    if (sibling.source === 'other' && existing.source !== 'other') {
      const index = result.indexOf(existing)
      if (index >= 0) {
        result[index] = sibling
        pairSlots.set(pairIdentity, sibling)
      }
    }

    if (sibling.source !== existing.source) {
      dropSameSourceExtras(pairIdentity)
    }
  })

  return result
}

export const getEarliestPositionChild = <T extends SettingNode>(
  children: T[] = []
): T | undefined =>
  [...children].sort((left, right) => getSettingPosition(left) - getSettingPosition(right))[0]
